/**
 * 公開請求画面 (/invoice/:token)。認証不要、印刷 CSS で A4 最適化。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §6 (v3.1.2 追加)
 *
 * PublicQuotePage と同じレイアウト思想。lineItems が空の場合は金額のみ表示。
 *
 * ヘッダーの「請求書 / 受注書」トグルで、同じ内容・同じレイアウトのまま書類名と文言だけを
 * 受注書用に切り替える (?doc=order。印刷・PDF にもそのまま反映される)。
 */
import type { PublicInvoiceView } from '@goenhub/shared';
import { Printer } from 'lucide-react';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { CategoryLogoHeader } from '@/components/common/CategoryLogoHeader';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { usePublicPageMode } from '@/hooks/usePublicPageMode';
import { usePublicInvoice } from '@/hooks/useSalesOps';
import { formatDate, formatYmd } from '@/lib/firestoreTime';
import { cn } from '@/lib/utils';

const fmtDate = (v: unknown) => {
  const f = formatDate(v);
  return f === '—' ? '-' : f;
};

/** T2: 納品先が請求先と同じ場合は表示を省略するための判定。 */
function isSameAsBilling(
  billing: PublicInvoiceView['customer'],
  delivery: NonNullable<PublicInvoiceView['delivery']>,
): boolean {
  return (
    (billing.postalCode ?? '') === (delivery.postalCode ?? '') &&
    (billing.prefecture ?? '') === (delivery.prefecture ?? '') &&
    (billing.address ?? '') === (delivery.address ?? '') &&
    (billing.buildingName ?? '') === (delivery.buildingName ?? '')
  );
}

const PAYMENT_LABEL: Record<string, string> = {
  transfer: '銀行振込',
  credit_card: 'クレジットカード',
  other: 'その他',
};

const BANK_ACCOUNT_TYPE_LABEL: Record<string, string> = {
  ordinary: '普通',
  checking: '当座',
  savings: '貯蓄',
};

/** ヘッダーのトグルで切り替える書類の種類。中身 (明細・金額・振込先) は共通で、文言だけが変わる。 */
type DocKind = 'invoice' | 'order';

const DOC_KINDS: DocKind[] = ['invoice', 'order'];

const DOC_TEXT: Record<
  DocKind,
  {
    label: string;
    heading: string;
    numberLabel: string;
    dueLabel: string;
    lead: string;
    amountLabel: string;
  }
> = {
  invoice: {
    label: '請求書',
    heading: '御 請 求 書',
    numberLabel: '請求番号',
    dueLabel: '支払期限',
    lead: '下記の通り、ご請求申し上げます。',
    amountLabel: 'ご請求金額 (税込)',
  },
  order: {
    label: '受注書',
    heading: '受 注 書',
    // 番号は請求書と同じもの (INV-…) を出す。受注書専用の採番は持たない
    numberLabel: '受注番号',
    dueLabel: 'お支払期限',
    lead: '下記の通り、ご注文を承りました。',
    amountLabel: 'ご注文金額 (税込)',
  },
};

export function PublicInvoicePage() {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading } = usePublicInvoice(token ?? null);
  // ?doc=order で受注書表示。URL に残すので、そのまま共有・再読込しても受注書で開く
  const [searchParams, setSearchParams] = useSearchParams();
  const docKind: DocKind = searchParams.get('doc') === 'order' ? 'order' : 'invoice';
  const text = DOC_TEXT[docKind];
  const setDocKind = (kind: DocKind) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (kind === 'order') next.set('doc', 'order');
        else next.delete('doc');
        return next;
      },
      { replace: true },
    );
  };

  // globals.css の body { overflow:hidden; height:100vh } は AppLayout 専用。
  // AppLayout 外のこのページでは解除しないと縦スクロールできない。
  usePublicPageMode();

  // PDF/印刷時のファイル名: 【請求書】お客様名様_YYYYMMDD (受注書表示中は【受注書】)
  useEffect(() => {
    const iv = data?.status === 'ok' ? data.invoice : null;
    if (!iv) return;
    const prev = document.title;
    const name = iv.customer?.name?.trim();
    const ymd = formatYmd(iv.issuedAt);
    document.title = [`【${text.label}】`, name ? `${name}様` : '', ymd ? `_${ymd}` : '']
      .join('')
      .trim();
    return () => {
      document.title = prev;
    };
  }, [data, text.label]);

  if (isLoading) return <Spinner />;
  if (!data) return <p>{text.label}を読み込めません</p>;
  if (data.status === 'not_found')
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <h1 className="text-xl font-semibold mb-2">{text.label}が見つかりません</h1>
        <p>URL をご確認ください。</p>
      </div>
    );
  if (data.status === 'expired')
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <h1 className="text-xl font-semibold mb-2">{text.label}の公開期限が切れています</h1>
        <p>お手数ですが、再発行をご依頼ください。</p>
      </div>
    );

  const invoice: PublicInvoiceView = data.invoice;
  const hasLineItems = invoice.lineItems && invoice.lineItems.length > 0;
  // 行割引の合計 (税抜)。> 0 のときだけ合計欄に割引前小計/割引合計の行を出す
  const discountTotal = (invoice.lineItems ?? []).reduce(
    (a, it) => a + (it.discountAmountExcludingTax ?? 0),
    0,
  );
  // 割引金額の非表示設定 (派生元見積のオプションを継承)。
  const hideDiscounts = invoice.hideDiscounts === true;
  // 型番の非表示設定 (派生元の見積から継承)。true のとき品目名の下の型番を出さない。
  const hideModelNames = invoice.hideModelNames === true;

  return (
    <>
      <style>{`
        @page { size: A4; margin: 15mm; }
        @media print {
          /* 100vh 固定・overflow:hidden のアプリ用レイアウトを印刷時は解除。
             残しておくと内容が切れたり空白ページが生まれる。 */
          html, body, #root {
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
          }
          body { background: white; }
          .no-print { display: none !important; }
          .invoice-page { box-shadow: none !important; padding: 0 !important; }
        }
      `}</style>
      <div className="min-h-screen print:min-h-0 bg-[var(--bg-root)] py-8 print:py-0">
        <div className="no-print max-w-[210mm] mx-auto mb-4 flex items-center justify-between gap-3">
          <div
            role="radiogroup"
            aria-label="書類の種類"
            className="inline-flex rounded-sm border border-[var(--border)] bg-[var(--bg-elevated)] p-0.5"
          >
            {DOC_KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                role="radio"
                aria-checked={docKind === kind}
                onClick={() => setDocKind(kind)}
                className={cn(
                  'px-4 py-1.5 rounded-sm text-[13px] font-medium transition-colors cursor-pointer',
                  docKind === kind
                    ? 'bg-[var(--accent)] text-white'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]',
                )}
              >
                {DOC_TEXT[kind].label}
              </button>
            ))}
          </div>
          <Button type="button" variant="primary" onClick={() => window.print()}>
            <Printer />
            PDF/印刷
          </Button>
        </div>

        {/* 画面では A4 相当の紙面に見せるため min-h を持たせるが、印刷時は解除する。
            @page margin 15mm を引いた実印刷領域は 267mm しかないため、
            297mm を残すと必ず余白だけの 2 ページ目が生成される。 */}
        <div className="invoice-page max-w-[210mm] mx-auto min-h-[297mm] print:min-h-0 bg-white text-black p-12 shadow-md print:shadow-none">
          <CategoryLogoHeader logo={invoice.categoryLogo ?? null} />
          <header className="border-b-2 border-black pb-4 mb-6">
            <h1 className="text-2xl font-bold tracking-wide text-center">{text.heading}</h1>
          </header>

          <div className="grid grid-cols-2 gap-8 mb-6">
            <div>
              <p className="text-sm">
                {invoice.customer.companyName ? (
                  <>
                    <span className="font-semibold">{invoice.customer.companyName}</span>
                    <br />
                  </>
                ) : null}
                {invoice.customer.department && (
                  <>
                    {invoice.customer.department}
                    <br />
                  </>
                )}
                <span className="text-base font-medium">{invoice.customer.name} 様</span>
              </p>
              {invoice.customer.postalCode && (
                <p className="text-xs mt-2">
                  〒{invoice.customer.postalCode}
                  <br />
                  {invoice.customer.prefecture}
                  {invoice.customer.address}
                  {invoice.customer.buildingName ?? ''}
                </p>
              )}
              {/* T2: 納品先 (請求先と異なる場合のみ表示) */}
              {invoice.delivery && !isSameAsBilling(invoice.customer, invoice.delivery) && (
                <div className="mt-3 pt-2 border-t border-gray-300 text-xs">
                  <p className="font-semibold mb-0.5">
                    お届け先{invoice.delivery.label ? ` (${invoice.delivery.label})` : ''}
                  </p>
                  {invoice.delivery.recipientName && <p>宛: {invoice.delivery.recipientName}</p>}
                  {invoice.delivery.postalCode && (
                    <p>
                      〒{invoice.delivery.postalCode} {invoice.delivery.prefecture ?? ''}
                      {invoice.delivery.address ?? ''}
                      {invoice.delivery.buildingName ?? ''}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="text-right text-sm">
              <p>
                {text.numberLabel}: {invoice.invoiceNumber}
              </p>
              <p>発行日: {fmtDate(invoice.issuedAt)}</p>
              <p>
                {text.dueLabel}: {fmtDate(invoice.dueDate)}
              </p>
            </div>
          </div>

          <div className="mb-6 text-right">
            <p className="text-xs">{invoice.vendor.companyName}</p>
            {invoice.vendor.postalCode && <p className="text-xs">〒{invoice.vendor.postalCode}</p>}
            {invoice.vendor.address && <p className="text-xs">{invoice.vendor.address}</p>}
            {invoice.vendor.phone && <p className="text-xs">TEL: {invoice.vendor.phone}</p>}
            {invoice.vendor.email && <p className="text-xs">{invoice.vendor.email}</p>}
            {invoice.vendor.representativeName && (
              <p className="text-xs mt-1">{invoice.vendor.representativeName}</p>
            )}
          </div>

          <p className="text-base font-medium mb-2">
            件名: <span className="font-normal">{invoice.title}</span>
          </p>
          <p className="text-sm mb-4">{text.lead}</p>

          <div className="bg-gray-100 p-3 mb-4 inline-block">
            <span className="text-sm">{text.amountLabel}</span>
            <span className="ml-3 text-xl font-bold tabular-nums">
              ¥{invoice.totalIncludingTax.toLocaleString()}
            </span>
          </div>

          {hasLineItems ? (
            <table className="w-full text-sm border-collapse mb-6">
              <thead>
                <tr className="border-y-2 border-black">
                  <th className="text-left p-2">品目</th>
                  <th className="text-right p-2 w-20">数量</th>
                  <th className="text-right p-2 w-28">単価 (税抜)</th>
                  <th className="text-right p-2 w-32">金額 (税込)</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lineItems.map((it, i) => (
                  <tr key={i} className="border-b border-gray-300">
                    <td className="p-2">
                      {it.name}
                      {it.variation && (
                        <span className="ml-1 text-xs text-gray-500">({it.variation})</span>
                      )}
                      {!hideModelNames && it.modelName && (
                        <div className="text-xs text-gray-500">{it.modelName}</div>
                      )}
                      {!hideDiscounts && (it.discountAmountExcludingTax ?? 0) > 0 && (
                        <div className="text-xs text-gray-700">
                          割引
                          {it.discountType === 'percent' && it.discountValue != null
                            ? ` (${it.discountValue}%)`
                            : ''}
                          : −¥
                          {(it.discountAmountExcludingTax ?? 0).toLocaleString()}
                          （税抜）
                        </div>
                      )}
                    </td>
                    <td className="text-right p-2 tabular-nums">{it.quantity}</td>
                    <td className="text-right p-2 tabular-nums">
                      ¥
                      {(hideDiscounts && it.quantity > 0
                        ? Math.round(it.subtotalExcludingTax / it.quantity)
                        : it.priceExcludingTax
                      ).toLocaleString()}
                    </td>
                    <td className="text-right p-2 tabular-nums">
                      ¥{it.subtotalIncludingTax.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {!hideDiscounts && discountTotal > 0 && (
                  <>
                    <tr className="border-t border-black">
                      <td colSpan={3} className="p-2 text-right">
                        割引前小計 (税抜)
                      </td>
                      <td className="text-right p-2 tabular-nums">
                        ¥{(invoice.subtotalExcludingTax + discountTotal).toLocaleString()}
                      </td>
                    </tr>
                    <tr className="border-b border-gray-300">
                      <td colSpan={3} className="p-2 text-right">
                        割引合計 (税抜)
                      </td>
                      <td className="text-right p-2 tabular-nums">
                        −¥{discountTotal.toLocaleString()}
                      </td>
                    </tr>
                  </>
                )}
                <tr className="border-y border-black">
                  <td colSpan={3} className="p-2 text-right">
                    小計 (税抜)
                  </td>
                  <td className="text-right p-2 tabular-nums">
                    ¥{invoice.subtotalExcludingTax.toLocaleString()}
                  </td>
                </tr>
                <tr className="border-b border-black">
                  <td colSpan={3} className="p-2 text-right">
                    消費税
                  </td>
                  <td className="text-right p-2 tabular-nums">
                    ¥{invoice.taxAmount.toLocaleString()}
                  </td>
                </tr>
                <tr className="border-b-2 border-black bg-gray-50">
                  <td colSpan={3} className="p-2 text-right font-bold">
                    合計 (税込)
                  </td>
                  <td className="text-right p-2 tabular-nums font-bold">
                    ¥{invoice.totalIncludingTax.toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <table className="w-full text-sm border-collapse mb-6">
              <thead>
                <tr className="border-y-2 border-black">
                  <th className="text-left p-2">摘要</th>
                  <th className="text-right p-2 w-40">金額 (税込)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-300">
                  <td className="p-2">{invoice.title}</td>
                  <td className="text-right p-2 tabular-nums">
                    ¥{invoice.totalIncludingTax.toLocaleString()}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="border-y border-black">
                  <td className="p-2 text-right">小計 (税抜)</td>
                  <td className="text-right p-2 tabular-nums">
                    ¥{invoice.subtotalExcludingTax.toLocaleString()}
                  </td>
                </tr>
                <tr className="border-b border-black">
                  <td className="p-2 text-right">消費税</td>
                  <td className="text-right p-2 tabular-nums">
                    ¥{invoice.taxAmount.toLocaleString()}
                  </td>
                </tr>
                <tr className="border-b-2 border-black bg-gray-50">
                  <td className="p-2 text-right font-bold">合計 (税込)</td>
                  <td className="text-right p-2 tabular-nums font-bold">
                    ¥{invoice.totalIncludingTax.toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}

          {invoice.paymentMethod && (
            <div className="text-sm space-y-1 mb-3">
              <p>
                <span className="font-semibold">お支払方法:</span>{' '}
                {PAYMENT_LABEL[invoice.paymentMethod] ?? invoice.paymentMethod}
              </p>
            </div>
          )}

          {/* 2026-05-28: 振込先口座情報 (owner の defaultBank をスナップショット) */}
          {invoice.vendor.bankAccount && (
            <div className="text-sm border border-gray-300 rounded p-3 mb-6 bg-gray-50">
              <p className="font-semibold mb-1">お振込先</p>
              <table className="text-xs">
                <tbody>
                  <tr>
                    <td className="pr-3 text-gray-600">銀行名</td>
                    <td>{invoice.vendor.bankAccount.bankName}</td>
                  </tr>
                  {invoice.vendor.bankAccount.branchName && (
                    <tr>
                      <td className="pr-3 text-gray-600">支店名</td>
                      <td>{invoice.vendor.bankAccount.branchName}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="pr-3 text-gray-600">口座種別</td>
                    <td>
                      {BANK_ACCOUNT_TYPE_LABEL[invoice.vendor.bankAccount.accountType] ??
                        invoice.vendor.bankAccount.accountType}
                    </td>
                  </tr>
                  <tr>
                    <td className="pr-3 text-gray-600">口座番号</td>
                    <td className="tabular-nums">{invoice.vendor.bankAccount.accountNumber}</td>
                  </tr>
                  <tr>
                    <td className="pr-3 text-gray-600">口座名義</td>
                    <td>{invoice.vendor.bankAccount.accountHolder}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {invoice.remarks && (
            <div className="text-xs text-gray-700 border-t pt-3 mb-4">
              <p className="font-semibold mb-1">備考</p>
              <p className="whitespace-pre-wrap">{invoice.remarks}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
